import { createContext, useCallback, useContext, useEffect, useRef, useState } from "react";
import {
    api, authRefreshApi, cleanupLegacyTokenStorage, refreshAccessToken,
    SESSION_INVALID_EVENT, setAccessToken, markLocalSignedOut, markLocalSignedIn, isLocalSignedOut,
} from "./api";

const AuthContext = createContext(null);
const LOGOUT_KEY = "cd_logout_event";

export function AuthProvider({ children }) {
    const [user, setUser] = useState(null);
    const [loading, setLoading] = useState(true);
    const [impersonation, setImpersonation] = useState(null);
    const [sessionExpired, setSessionExpired] = useState(false);
    const sessionGeneration = useRef(0);

    const loadMe = useCallback(async () => {
        const generation = sessionGeneration.current;
        setLoading(true);
        try {
            if (isLocalSignedOut()) {
                setUser(null);
                return;
            }
            await refreshAccessToken();
            const { data } = await api.get("/auth/me");
            if (generation !== sessionGeneration.current || isLocalSignedOut()) return;
            setUser(data);
            setSessionExpired(false);
        } catch {
            if (generation !== sessionGeneration.current) return;
            setAccessToken(null);
            setUser(null);
        } finally {
            if (generation === sessionGeneration.current) setLoading(false);
        }
    }, []);

    useEffect(() => { cleanupLegacyTokenStorage(); loadMe(); }, [loadMe]);
    useEffect(() => {
        const invalid = (event) => {
            setUser(null); setImpersonation(null); setLoading(false);
            setSessionExpired(event?.detail?.reason === "expired");
        };
        const storage = (event) => { if (event.key === LOGOUT_KEY) invalid(); };
        window.addEventListener(SESSION_INVALID_EVENT, invalid);
        window.addEventListener("storage", storage);
        return () => {
            window.removeEventListener(SESSION_INVALID_EVENT, invalid);
            window.removeEventListener("storage", storage);
        };
    }, []);

    const acceptAuth = (data) => {
        sessionGeneration.current += 1;
        markLocalSignedIn();
        setAccessToken(data.access_token || data.token);
        setSessionExpired(false); setUser(data.user);
        return data.user;
    };
    const login = async (identifier, password) => acceptAuth((await api.post("/auth/login", { identifier, password })).data);
    const register = async (payload) => acceptAuth((await api.post("/auth/register", payload)).data);
    const addRole = async (role) => {
        const { data } = await api.post("/auth/roles", { role });
        setAccessToken(data.access_token || data.token); await loadMe(); return data.roles;
    };
    const logout = async () => {
        // Immediately clear local auth before requesting server-side cookie revocation.
        // A slow/offline network may never block the user's ability to exit the admin UI.
        sessionGeneration.current += 1;
        markLocalSignedOut();
        setUser(null); setImpersonation(null); setSessionExpired(false); setLoading(false);
        localStorage.setItem(LOGOUT_KEY, String(Date.now()));
        try {
            await authRefreshApi.post("/auth/logout", {}, { timeout: 5000 });
        } catch {
            // The local signed-out marker prevents an unrevoked cookie from
            // automatically authenticating this tab again until explicit login.
        }
    };
    const startImpersonation = async (targetId, reason) => {
        const { data } = await api.post(`/admin/users/${targetId}/impersonate`, { reason });
        setAccessToken(data.access_token || data.token); setImpersonation(data.impersonation);
        const me = await api.get("/auth/me"); setUser(me.data); return data;
    };
    const stopImpersonation = async ({ silent } = {}) => {
        try { if (!silent) await api.post("/admin/impersonation/exit"); } catch { /* best effort */ }
        setImpersonation(null); await loadMe();
    };
    return <AuthContext.Provider value={{
        user, loading, login, register, addRole, logout, reload: loadMe, sessionExpired,
        impersonation, isImpersonating: !!impersonation, startImpersonation, stopImpersonation,
    }}>{children}</AuthContext.Provider>;
}

export const useAuth = () => useContext(AuthContext);
